// ============================================================
// useSEO — Dynamic per-page SEO meta tag updater
// Usage:
//   useSEO({ title, description, keywords, ogTitle, ogDescription,
//            canonicalPath, noindex, ogType, hreflang, structuredData })
// ============================================================
import { useEffect } from 'react';

// Set REACT_APP_BASE_URL in .env.production to your subdomain, e.g. https://shayaristudio.online
const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://shayaristudio.online';
const OG_IMAGE = `${BASE_URL}/logo512.png`;

const setMeta = (selector, attr, value) => {
  if (!value) return;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.startsWith('meta[property') ? 'meta' : 'meta');
    document.head.appendChild(el);
    // Set the identifying attribute
    const [, key, val] = selector.match(/\[([^=]+)="([^"]+)"\]/) || [];
    if (key && val) el.setAttribute(key, val);
  }
  el.setAttribute(attr, value);
};

const setLink = (rel, href) => {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
};

const useSEO = ({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  canonicalPath,
  noindex,
  ogType,
  ogLocale,
  hreflang,
  structuredData,
}) => {
  useEffect(() => {
    // ── Document title ──────────────────────────────────────
    if (title) document.title = title;

    // ── Robots meta (noindex support) ──────────────────────
    setMeta(
      'meta[name="robots"]',
      'content',
      noindex
        ? 'noindex, nofollow'
        : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
    );

    // ── Primary meta tags ───────────────────────────────────
    setMeta('meta[name="description"]',      'content', description);
    setMeta('meta[name="keywords"]',         'content', keywords);

    // ── Open Graph ──────────────────────────────────────────
    setMeta('meta[property="og:site_name"]', 'content', 'ShayariStudio');
    setMeta('meta[property="og:title"]',       'content', ogTitle || title);
    setMeta('meta[property="og:description"]', 'content', ogDescription || description);
    setMeta('meta[property="og:url"]',         'content', canonicalPath ? `${BASE_URL}${canonicalPath}` : BASE_URL);
    setMeta('meta[property="og:image"]',       'content', OG_IMAGE);
    setMeta('meta[property="og:image:width"]', 'content', '512');
    setMeta('meta[property="og:image:height"]','content', '512');
    setMeta('meta[property="og:image:alt"]',   'content', 'ShayariStudio — Hindi Urdu Shayari');
    setMeta('meta[property="og:type"]',        'content', ogType || 'website');
    setMeta('meta[property="og:locale"]',      'content', ogLocale || 'hi_IN');

    // ── Twitter Card ────────────────────────────────────────
    setMeta('meta[name="twitter:card"]',        'content', 'summary_large_image');
    setMeta('meta[name="twitter:site"]',        'content', '@ShayariStudio');
    setMeta('meta[name="twitter:title"]',       'content', ogTitle || title);
    setMeta('meta[name="twitter:description"]', 'content', ogDescription || description);
    setMeta('meta[name="twitter:image"]',       'content', OG_IMAGE);
    setMeta('meta[name="twitter:image:alt"]',   'content', 'ShayariStudio — Hindi Urdu Shayari');

    // ── Canonical URL ────────────────────────────────────────
    if (canonicalPath) setLink('canonical', `${BASE_URL}${canonicalPath}`);

    // ── hreflang alternate links ─────────────────────────────
    if (hreflang && Array.isArray(hreflang)) {
      hreflang.forEach(({ lang, href }) => {
        const existing = document.querySelector(`link[rel="alternate"][hreflang="${lang}"][data-page-hreflang]`);
        if (existing) {
          existing.href = href;
        } else {
          const el = document.createElement('link');
          el.rel = 'alternate';
          el.setAttribute('hreflang', lang);
          el.href = href;
          el.setAttribute('data-page-hreflang', 'true');
          document.head.appendChild(el);
        }
      });
    }

    // ── Structured Data (JSON-LD) ────────────────────────────
    if (structuredData) {
      const existing = document.querySelector('script[data-page-ld]');
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-page-ld', 'true');
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

    // Cleanup: remove page-specific JSON-LD and hreflang tags when unmounting
    return () => {
      const ld = document.querySelector('script[data-page-ld]');
      if (ld) ld.remove();
      document.querySelectorAll('link[data-page-hreflang]').forEach(el => el.remove());
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, keywords, ogTitle, ogDescription, canonicalPath, noindex, ogType, ogLocale, hreflang, structuredData]);
};

export default useSEO;
